import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DataTable, IdControl } from '@shared-ui/components';
import type { ColumnDef, SortState } from '@shared-ui/components';
import { useModules } from '@shared-ui/reference-data';
import { LatexText } from '@shared-ui/latex';
import type { LedgerQuestion, QualityCell } from '../../../api/questionQualityApi';
import { RESOLUTION_LABEL, RESOLUTION_RANK } from '../../../api/questionQualityApi';
import { OwnerPills } from './OwnerPills';
import { ResolutionBadge } from './ResolutionEditor';
import { findingsPath, sevColor, sevRank, verdictColor } from './verdictDisplay';
import './QuestionQuality.css';

/**
 * Question Quality → By Question. The question is the row and every rule is a
 * column, so one glance says "this question fails three checks" — the view you
 * want when triaging a bad question rather than a weak rule (that is ByRule).
 *
 * Only questions carrying at least one non-pass verdict are listed: a question
 * that passes everything has nothing to triage. It renders the rows it is HANDED
 * — the page above owns the fetch and the filters, so this table and the rule
 * table are two projections of one narrowed population.
 */


/** Cell glyphs. A pass is deliberately faint — the eye should land on the
 *  failures, and the passes only matter as "this rule was actually run". */
const VERDICT_GLYPH = { fail: '✕', unsure: '?', pass: '✓' } as const;

/** Sort rank for a rule column, worst first. Absent (never checked) sorts last. */
function cellRank(cell?: QualityCell): number {
    if (!cell) return 0;
    if (cell.verdict === 'fail') return 10 + sevRank[cell.severity];
    if (cell.verdict === 'unsure') return 5;
    return 1;
}

function Cell({ ruleId, cell }: { ruleId: string; cell?: QualityCell }) {
    if (!cell) return <span className="qq-cell qq-cell--none" title={`${ruleId}: not checked`}>·</span>;
    const color = cell.verdict === 'fail' ? sevColor[cell.severity] : verdictColor[cell.verdict];
    // A settled finding recedes: it is still a fail and still says so, but the
    // grid is read to find what is left to do.
    const settled = cell.resolution && cell.resolution.status !== 'active';
    const title = [
        ruleId,
        cell.verdict === 'fail' ? `fail (${cell.severity})` : cell.verdict,
        cell.resolution ? RESOLUTION_LABEL[cell.resolution.status] : undefined,
        cell.resolution?.comment,
        cell.priorCount ? `${cell.priorCount} earlier cycle${cell.priorCount > 1 ? 's' : ''}` : undefined,
        cell.rationale,
    ].filter(Boolean).join(' — ');
    return (
        <span
            className={`qq-cell qq-cell--${cell.verdict}${settled ? ' qq-cell--settled' : ''}`}
            style={{ color }}
            title={title}
        >
            {VERDICT_GLYPH[cell.verdict]}
            {/* This finding has been here before — the question was edited and
                re-judged at least once under this rule. A glyph rather than a
                count: the grid answers "where do I look", and how many times is
                a question for the findings page. */}
            {!!cell.priorCount && <sup className="qq-cell-prior">↻</sup>}
        </span>
    );
}

export function ByQuestion({ rows, rules, subject, page, pageSize, onPageChange, truncated, total }: {
    /** ONE PAGE, already filtered and paged by the server. */
    rows: LedgerQuestion[];
    rules: string[];
    subject: string;
    page: number;
    /** Rows the server put on this page — the client no longer slices. */
    pageSize: number;
    onPageChange: (page: number) => void;
    truncated: boolean;
    total: number;
}) {
    const { moduleNameMap } = useModules();
    const navigate = useNavigate();
    const location = useLocation();
    const [sort, setSort] = useState<SortState>({ sortBy: 'issues', sortOrder: 'desc' });

    // A clean question is not triage — it is in the ledger and the rule table
    // counts it, but it is not a row here. Narrowed by the SERVER now
    // (`flaggedOnly`): dropping them after paging gave short pages and a total
    // that counted rows nobody could see.

    const columns: ColumnDef<LedgerQuestion>[] = useMemo(() => [
        {
            key: 'question',
            header: 'Question',
            sortable: true,
            sortValue: r => (r.questionText ?? r.questionId).toLowerCase(),
            hideable: false,
            width: '32%',
            render: r => (
                <div className="qq-qcell">
                    <span className="qq-idrow">
                        <IdControl value={r.questionId} container="questions" />
                        {/* Beside the id rather than in a column of its own: it is
                            one short word identifying the row, and a whole column
                            of "MCQ" was width the stem could use. */}
                        {r.questionType && <span className="qq-typepill">{r.questionType}</span>}
                        {/* Ownership, and only when there is any: whose tenant it
                            is and whose question it is change how a finding is
                            read, and both are invisible from the stem. */}
                        <OwnerPills q={r} />
                    </span>
                    {/* Stems are LaTeX. Raw, a chemistry question reads
                        "$\text{PF}_5$" — the notation is the part that tells you
                        WHICH question this is. */}
                    {r.questionText && (
                        <span className="qq-qtext"><LatexText text={r.questionText} truncate={160} /></span>
                    )}
                </div>
            ),
        },
        {
            key: 'topic',
            header: 'Topic',
            sortable: true,
            sortValue: r => (r.moduleId ? moduleNameMap.get(r.moduleId) ?? r.moduleId : '').toLowerCase(),
            render: r => {
                const name = r.moduleId ? moduleNameMap.get(r.moduleId) ?? r.moduleId : '';
                return <span className="qq-topic" title={name}>{name || '—'}</span>;
            },
        },
        {
            key: 'resolution',
            header: 'Resolution',
            sortable: true,
            // Unresolved first: the list is a queue.
            sortValue: r => RESOLUTION_RANK[r.resolutionStatus] * 100 + r.activeCount,
            render: r => (
                <ResolutionBadge
                    status={r.resolutionStatus}
                    title={r.activeCount
                        ? `${r.activeCount} of ${r.failCount + r.unsureCount} findings still open`
                        : 'Every finding on this question has been ruled on'}
                />
            ),
        },
        {
            key: 'issues',
            header: 'Issues',
            sortable: true,
            // Severity first, then volume — the same worst-first order the
            // server ships rows in, so the default sort matches the payload.
            sortValue: r => (r.worstSeverity ? sevRank[r.worstSeverity] : 0) * 1000 + r.failCount * 10 + r.unsureCount,
            render: r => (
                <span className="qq-issues">
                    {r.failCount > 0 && (
                        <span style={{ color: r.worstSeverity ? sevColor[r.worstSeverity] : verdictColor.fail, fontWeight: 700 }}
                            title={`${r.failCount} fail(s), worst severity ${r.worstSeverity}`}>
                            {r.failCount} fail
                        </span>
                    )}
                    {r.unsureCount > 0 && (
                        <span style={{ color: verdictColor.unsure }} title={`${r.unsureCount} unsure`}>
                            {r.unsureCount} unsure
                        </span>
                    )}
                </span>
            ),
        },
        ...rules.map((ruleId): ColumnDef<LedgerQuestion> => ({
            key: `rule:${ruleId}`,
            header: <span className="qq-rulehead" title={ruleId}>{ruleId.replace(/-/g, '‑')}</span>,
            toggleLabel: ruleId,
            mobileLabel: ruleId,
            sortable: true,
            sortValue: r => cellRank(r.cells[ruleId]),
            className: 'qq-cellcol',
            render: r => <Cell ruleId={ruleId} cell={r.cells[ruleId]} />,
        })),
        {
            key: 'detected',
            header: 'Detected',
            sortable: true,
            sortValue: r => r.issueDetectedAt ?? r.lastCheckedAt,
            render: r => {
                const iso = r.issueDetectedAt ?? r.lastCheckedAt;
                return (
                    <span className="qq-dim" title={`issue verdict recorded ${new Date(iso).toLocaleString()}`}>
                        {new Date(iso).toLocaleDateString()}
                    </span>
                );
            },
        },
    ], [rules, moduleNameMap]);

    // `rows` IS the page. Paging is the server's, so the pager only has to
    // describe it — slicing here would page a page.
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const safePage = Math.min(page, totalPages);
    const pageRows = rows;

    return (
        <>
            {/* No count line: the table's own pager already says "1/1 (14)".
                The cap warning stays — nothing else on the page says the ledger
                stopped short of the corpus, and a pager reading (2000) looks
                exactly like a complete answer. */}
            {/* Now says what it means. `total` is an exact count of the
                filtered set — this warns about the SCAN behind it hitting its
                ceiling, which is the one case where that count describes a
                prefix of the corpus rather than all of it. */}
            {truncated && (
                <p className="qq-count qq-warn">
                    This subject has more verdicts than one read of the ledger returns,
                    so these counts describe part of it. Narrow by topic or grade.
                </p>
            )}

            <div className="qq-tablewrap">
                <DataTable
                    columns={columns}
                    data={pageRows}
                    rowKey={r => r.questionId}
                    columnToggleKey="question-quality-by-question"
                    compact
                    emptyMessage="No flagged question matches these filters."
                    sort={sort}
                    onSortChange={next => { setSort(next); onPageChange(1); }}
                    /* A row opens its own page rather than expanding in place: the
                       rationales are paragraphs and this table is ten narrow
                       columns, so an expansion inherited a width that could not
                       hold them. */
                    onRowClick={r => navigate(findingsPath(r.questionId, subject, location.search))}
                    rowHref={r => findingsPath(r.questionId, subject, location.search)}
                    pagination={{ page: safePage, totalPages, total, onPageChange }}
                />
            </div>
        </>
    );
}
